import {assertTokenValidForMutation, handleResponse} from "@/services/service-helpers.ts";
import {AnnualDonation, MemberDonations, OneTimeDonation} from "@/models/donation.ts";
import log from '@/lib/logger';

export type CreateOneTimeDonationRequest = {
    amount: number;
    donationDate: string;
};

export type CreateAnnualDonationRequest = {
    amount: number;
    validFrom: string;
};

export type UpdateAnnualDonationRequest = {
    amount: number;
    validFrom: string;
};

export type UpdateOneTimeDonationRequest = {
    amount: number;
    donationDate: string;
};

export async function getDonationsForMember(
    token: string | null,
    memberId: string
): Promise<MemberDonations | void> {
    if (!token) {
        log.error("No token provided to request donations");
        return Promise.reject("No token provided");
    }

    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/members/${memberId}/donations`, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    return handleResponse<MemberDonations>(response);
}

export async function createOneTimeDonation(
    token: string | null,
    memberId: string,
    data: CreateOneTimeDonationRequest
): Promise<OneTimeDonation | void> {
    if (!token) {
        log.error("No token provided to create one-time donation");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/members/${memberId}/donations/one-time`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    });

    return handleResponse<OneTimeDonation>(response);
}

export async function deleteOneTimeDonation(
    token: string | null,
    memberId: string,
    donationId: string
): Promise<void> {
    if (!token) {
        log.error("No token provided to delete one-time donation");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/members/${memberId}/donations/one-time/${donationId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    return handleResponse<void>(response);
}

export async function updateOneTimeDonation(
    token: string | null,
    memberId: string,
    donationId: string,
    data: UpdateOneTimeDonationRequest
): Promise<OneTimeDonation | void> {
    if (!token) {
        log.error("No token provided to update one-time donation");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/members/${memberId}/donations/one-time/${donationId}`, {
        method: 'PATCH',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    });

    return handleResponse<OneTimeDonation>(response);
}

export async function createAnnualDonation(
    token: string | null,
    memberId: string,
    data: CreateAnnualDonationRequest
): Promise<AnnualDonation | void> {
    if (!token) {
        log.error("No token provided to create annual donation");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/members/${memberId}/donations/annual`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    });

    return handleResponse<AnnualDonation>(response);
}

export async function updateAnnualDonation(
    token: string | null,
    memberId: string,
    donationId: string,
    data: UpdateAnnualDonationRequest
): Promise<AnnualDonation | void> {
    if (!token) {
        log.error("No token provided to update annual donation");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/members/${memberId}/donations/annual/${donationId}`, {
        method: 'PATCH',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    });

    return handleResponse<AnnualDonation>(response);
}

export async function endAnnualDonation(
    token: string | null,
    memberId: string,
    donationId: string
): Promise<void> {
    if (!token) {
        log.error("No token provided to end annual donation");
        return Promise.reject("No token provided");
    }
    assertTokenValidForMutation()

    const response = await fetch(`${import.meta.env.VITE_NBH_URL_ROOT}/api/members/${memberId}/donations/annual/${donationId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });

    return handleResponse<void>(response);
}
